import { MARKER_BRAND_PACIFICBLUE } from "@/constants/custom-marker";
import { IconStore, getIconMetaData } from "@/icon-store/IconStore";
import type { MapboxMap } from "@/layers/MapboxMap";
import { MapInteractionEventType } from "@/models/map-interaction/mapInteractionStateEvents";
import type { MapInteractionStateMachine } from "@/models/map-interaction/mapInteractionStateMachine";
import Logger from "@mm/logging";
import { type MapMouseEvent, Point } from "mapbox-gl";
import type { InterpreterFrom } from "xstate";
const logger = Logger.fromFilename(__filename);

type IconEventData = { id: string };

export const fetchIconImg = (map: MapboxMap) => {
  return ({ id }: IconEventData) => {
    const metadata = getIconMetaData(id);
    if (!metadata) {
      return;
    }

    // Note: We add an empty image on the map until the actual image comes back from request.
    // https://github.com/mapbox/mapbox-gl-js/issues/8335#issuecomment-783570168
    map.addImage(id, { width: 0, height: 0, data: new Uint8Array() }, { sdf: metadata.sdf });

    IconStore.instance
      .getIcon(id)
      .then((iconImage) => {
        map.removeImage(id);

        if (id === MARKER_BRAND_PACIFICBLUE) {
          map.addImage(id, iconImage as HTMLImageElement, {
            sdf: metadata.sdf,
            pixelRatio: window.devicePixelRatio,
          });
        } else {
          map.addImage(id, iconImage as HTMLImageElement, {
            sdf: metadata.sdf,
          });
        }

        // Workaround for old open issue in mapbox
        // in which icons don't render right after they are loaded.
        // Loop trough all symbol sources and refresh data
        // https://github.com/mapbox/mapbox-gl-js/issues/6231
        for (const sourceId of Object.keys(map.style._symbolSourceCaches)) {
          // @ts-ignore
          if (map.getSource(sourceId)._updateWorkerData) {
            // @ts-ignore
            map.getSource(sourceId)._updateWorkerData();
          }
        }
      })
      .catch((e) => logger.error(e));
  };
};

/**
 * Create a event listener callback that emits a click event to the map interaction state machine
 * @param mapId
 * @param mapInteractionService
 * @returns
 */
export const emitClickToInteractionStateMachine =
  (mapId: number, mapInteractionService: InterpreterFrom<MapInteractionStateMachine>) => (e: MapMouseEvent) => {
    const mapBoxLngLat = e.lngLat.wrap();
    mapInteractionService.send({
      type: MapInteractionEventType.click,
      // Map ID is the DB record ID
      mapId: mapId,
      coordinates: {
        lat: mapBoxLngLat.lat,
        lon: mapBoxLngLat.lng,
      },
    });
  };

export const emitContextMenuToInteractionStateMachine =
  (mapId: number, mapInteractionService: InterpreterFrom<MapInteractionStateMachine>) => (e: MapMouseEvent) => {
    // If event isn't originating from client interaction, don't do anything
    if (!e.originalEvent) {
      return;
    }
    const {
      lngLat,
      point, // pixel position in the map canvas
      originalEvent: { clientX, clientY },
      target,
    } = e;

    const map: any = target as any; // Mapbox Map instance
    const wrapped = lngLat.wrap();

    // Default: use cursor coordinates
    let outLon = wrapped.lng;
    let outLat = wrapped.lat;

    // Try to pick a POI (Point feature) under cursor with a small tolerance
    try {
      const tol = 5;
      const bbox: [[number, number], [number, number]] = [
        [point.x - tol, point.y - tol],
        [point.x + tol, point.y + tol],
      ];

      // TIP: restrict to your POI layers e.g. { layers: ['poi-layer-1', 'poi-layer-2'] }
      const feats =
        typeof map?.queryRenderedFeatures === "function"
          ? map.queryRenderedFeatures(bbox /*, { layers: ['poi-layer'] }*/)
          : [];

      const pf = feats?.find((f: any) => f?.geometry?.type === "Point");
      // Tries to take geometry from an existing POI on the map
      if (pf?.geometry?.coordinates?.length === 2) {
        const [lngF, latF] = pf.geometry.coordinates as [number, number];
        outLon = lngF;
        outLat = latF;
      }
    } catch {
      // swallow picking errors; keep fallback coords
      console.warn("No POI");
    }

    mapInteractionService.send({
      type: MapInteractionEventType.contextMenu,
      mapId,
      coordinates: { lat: outLat, lon: outLon },
      point: new Point(clientX, clientY),
    });
  };
